import React, { useEffect, useState, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PuffLoader } from 'react-spinners';
import { Input, Page, Button, AuthContext } from '../shared';
import axios from '../axios';
import styles from './ResetPassword.module.scss';
import mainStyles from '../Main/Main.module.scss';
import MainHoc from '../Main/MainHoc';
import classNames from 'classnames';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as BeeHeroLogo } from '../images/BeeHeroLogo.svg';
import {
    ADMIN_API,
    emailIsRequiredText,
    enterPasswordText,
    passwordConfirmationKey,
    passwordIsRequiredText,
    passwordKey,
    passwordLabelText,
    SERVICE_STATE
} from '../shared/consts';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { PasswordConditions, validatePassword, passwordRegex } from './PasswordConditions';
import {
    backToLoginText,
    confirmPasswordText,
    reEnterPasswordText,
    setNewPasswordText,
    tokenExpiredAndSendEmailText,
    tokenExpiredText
} from './textConsts';
import useMediaQuery from '../shared/hooks/useMediaQuery';
import { isMobileQuery } from '../shared/MediaQuery/mediaQueryUtils';

const schema = yup
    .object({
        [passwordKey]: yup.string().matches(passwordRegex).required(passwordIsRequiredText),
        [passwordConfirmationKey]: yup.string().oneOf([yup.ref(passwordKey), null], emailIsRequiredText)
    })
    .required();

const ResetPassword = (props) => {
    const {
        formState: { errors },
        register,
        handleSubmit,
        control
    } = useForm({
        resolver: yupResolver(schema)
    });

    const token = props.match.params.token;
    const { userInfo, serviceState } = useContext(AuthContext);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [loading, setLoading] = useState(true);
    const isMobile = useMediaQuery(isMobileQuery);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const { allConditionsMet } = validatePassword(password);
    const passwordsMatch = password === confirmPassword && password !== '' && confirmPassword !== '';
    const isButtonDisabled = !allConditionsMet || !passwordsMatch;

    const goToHome = () => {
        props.history.replace('/');
    };

    if (userInfo) {
        goToHome();
    }

    const handleVerification = async () => {
        try {
            const res = await axios.post(`${ADMIN_API}/verify_reset_password_user`, {
                verify_user_token: token
            });
            if (res.data?.invite_token_expired) {
                setLoading(false);
                setTokenExpired(tokenExpiredAndSendEmailText);
            } else if (res.data?.reset_token_expired) {
                setLoading(false);
                setTokenExpired(tokenExpiredText);
            } else {
                setLoading(false);
            }
        } catch (e) {
            goToHome();
        }
    };

    useEffect(() => {
        !userInfo && handleVerification();
    }, []);

    const handlePasswordChange = (e, onChange) => {
        setPassword(e.target.value);
        onChange(e);
    };

    const handleConfirmPasswordChange = (e, onChange) => {
        setConfirmPassword(e.target.value);
        onChange(e);
    };

    const handleReset = (data) => {
        setLoading(true);
        axios
            .post(
                `${ADMIN_API}/reset_password`,
                { password: data.password, password_reset_token: token },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            .then((res) => {
                if (res.status === 200) {
                    setLoading(false);
                    toast.success(res.data.message);
                    setTimeout(() => {
                        props.history.replace('/login');
                    }, 1000);
                }
            })
            .catch((err) => {
                setLoading(false);
                if (serviceState === SERVICE_STATE.OK) {
                    toast.error(err.response.data.message || err.response.data.msg);
                }
            });
    };

    return (
        <MainHoc>
            <div className={classNames({ [mainStyles.content]: !isMobile })}>
                {loading && (
                    <div
                        className={classNames(mainStyles.backdrop, {
                            [styles.full_width]: isMobile
                        })}
                    >
                        <PuffLoader color={'#ffcc2e'} size={100} />
                    </div>
                )}

                <Page>
                    <BeeHeroLogo width={141} className={styles['page-beehero-logo']} />
                    <Page.Section>
                        <span className={styles.title}>{!tokenExpired ? setNewPasswordText : null} </span>
                        {tokenExpired ? (
                            <div className={styles.token_expired}>
                                {tokenExpired}
                                <div className={styles.back_button}>
                                    <Button color="yellow" onClick={goToHome}>
                                        {backToLoginText}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <form className={styles.form} onSubmit={handleSubmit(handleReset)}>
                                <Controller
                                    name={passwordKey}
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Input
                                            errors={errors[passwordKey]}
                                            name={passwordKey}
                                            type="password"
                                            label={enterPasswordText}
                                            placeholder={passwordLabelText}
                                            value={value}
                                            onChange={(change) => handlePasswordChange(change, onChange)}
                                            infoLabel={() => <PasswordConditions password={password} />}
                                            register={register(passwordKey).ref}
                                        />
                                    )}
                                />
                                <Controller
                                    name={passwordConfirmationKey}
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <Input
                                            errors={errors[passwordConfirmationKey]}
                                            name={passwordConfirmationKey}
                                            type="password"
                                            label={reEnterPasswordText}
                                            placeholder={confirmPasswordText}
                                            onChange={(change) => handleConfirmPasswordChange(change, onChange)}
                                            value={value}
                                            register={register(passwordConfirmationKey).ref}
                                        />
                                    )}
                                />
                                <div className={styles['submit_button']}>
                                    <Button color="yellow" type="submit" wide disabled={isButtonDisabled}>
                                        {confirmPasswordText}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </Page.Section>
                    {!tokenExpired && <Page.Section center></Page.Section>}
                </Page>
            </div>
        </MainHoc>
    );
};

export default ResetPassword;

ResetPassword.propTypes = {
    match: PropTypes.object,
    history: PropTypes.object
};
